import {useCallback, useRef} from "react";

import {useInterval} from "@khanacademy/wonder-blocks-timing";
import {useGuideState} from "@khan/guide-state";
import {Errors, KAError} from "@khan/logging";
import {useLocation} from "@khan/urls";

import {type BlooketQuestion} from "../blooket/types.ts";
import {mapBlooketQuestionsToSavableFormat} from "../blooket/utils.ts";
import type {AutosavingStatus, ToolPersona} from "../types.ts";

import {useCreateBlooketMutation} from "./use-create-blooket-mutation.ts";
import {useUpdateBlooketMutation} from "./use-update-blooket-mutation.ts";

export const useBlooketAutosave = (
    persona: ToolPersona,
    setSavingStatus: (savingStatus: AutosavingStatus) => void,
    title: string,
    description: string,
    questions: BlooketQuestion[],
    unsavedChanges: boolean,
    clearUnsavedChanges: () => void,
) => {
    const initialSaveComplete = useRef<boolean>(false);
    const threadId = useGuideState("threadId");
    const {pathname, search} = useLocation();
    const createBlooket = useCreateBlooketMutation();
    const updateBlooket = useUpdateBlooketMutation();

    // TODO Add analytics events.

    const doAutosaveMutation = useCallback(() => {
        if (
            !unsavedChanges ||
            !threadId ||
            // TOOD(reid): Extract constants for these known paths
            (!pathname.includes("/teacher/khanmigo-tools") &&
                !pathname.includes("/parent/khanmigo-tools") &&
                !pathname.includes("/teacher/tools"))
        ) {
            return;
        }

        setSavingStatus("loading");

        if (new URLSearchParams(search).has("resumeThreadId")) {
            initialSaveComplete.current = true;
        }

        if (!initialSaveComplete.current) {
            createBlooket({
                input: {
                    threadId: threadId ?? "",
                    title: title ?? "",
                    description: description ?? "",
                    questions: mapBlooketQuestionsToSavableFormat(questions),
                    persona,
                },
            })
                .then((_) => {
                    setSavingStatus("saved");
                    initialSaveComplete.current = true;
                    clearUnsavedChanges();
                })
                .catch((error) => {
                    setSavingStatus("error");
                    throw new KAError(
                        "Failed to autosave blooket",
                        Errors.Internal,
                        {
                            cause: error,
                        },
                    );
                });
        } else {
            updateBlooket({
                input: {
                    threadId: threadId ?? "",
                    title: title ?? "",
                    description: description ?? "",
                    questions: mapBlooketQuestionsToSavableFormat(questions),
                },
            })
                .then((_) => {
                    setSavingStatus("saved");
                    clearUnsavedChanges();
                })
                .catch((error) => {
                    setSavingStatus("error");
                    throw new KAError(
                        "Failed to autosave blooket",
                        Errors.Internal,
                        {
                            cause: error,
                        },
                    );
                });
        }
    }, [
        unsavedChanges,
        threadId,
        pathname,
        setSavingStatus,
        search,
        createBlooket,
        title,
        description,
        questions,
        persona,
        clearUnsavedChanges,
        updateBlooket,
    ]);

    useInterval(doAutosaveMutation, 5000);
};
